/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect } from "react";
import { Box, Container, Grid, Typography, useMediaQuery, useTheme, Divider, Button, Skeleton } from "@mui/material";
import { NavBarOcularVisit } from "@components/NavBarOcularVisit";
import { InactivityHandler } from "@components/InactivityHandler";
import CustomRightImage from "@components/CustomRightImagev2";
import ReactPlayer from "react-player";
import { CustomSwitch } from "@components/CustomSwitch";
import { formatCurrency } from "@helpers/funciones";
import {
  useLazyGetLoanApplicationByIdQuery,
  useLazyGetLoanApplicationConditionsByIdQuery,
  usePostValidateNextStageMutation,
} from "@api/applications";
import { useSelector } from "react-redux";
import * as Sentry from "@sentry/react";
import { ApiError } from "@interfaces/response";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { RootState, useAppDispatch } from "@store/store";
import { useLazyGetTablaAmortizacionQuery, usePostDocumentGenarateMutation } from "@api/documents";
import iconD from "@assets/img/icon_donwload.svg";
import { STATUS_CATALOG } from "@helpers/constantes";
import { useNavigate } from "react-router-dom";
import useInterfaceConfig from "@hooks/useInterfaceConfig";

interface viewParameters {
  monto: number;
  plazo: number;
  tasa: number;
  comisionPorcentual: number | null;
  valorComision: number | null;
  primerPago: number | null;
  pagosConsecuentes: number | null;
}

const CardInfo = ({
  title,
  value,
  valueType,
  width,
}: {
  title: string;
  value: number | null | undefined;
  valueType?: "months" | "currency" | "percentage";
  width?: string;
}) => {
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const interfaceConfig = useInterfaceConfig();

  const formatValue = (value: number | null | undefined) => {
    if (value === null || value === undefined) return "--";
    switch (valueType) {
      case "currency": {
        const currency = interfaceConfig.cotizador?.current_currency || "MXN";
        return `${formatCurrency(value, "$")} ${currency}`;
      }
      case "percentage":
        return `${value} %`;
      case "months": {
        const term_type = interfaceConfig.cotizador?.term_type || "Meses";
        const capitalizado = term_type.charAt(0).toUpperCase() + term_type.slice(1).toLowerCase();
        return `${value} ${capitalizado}`;
      }

      default:
        return value.toString();
    }
  };

  return (
    <Box width={width || "100%"}>
      <Typography
        textAlign={"center"}
        variant={isMobileOrTablet ? "body2" : "body1"}
        color={theme?.palette?.primary?.dark}
      >
        {title}
      </Typography>

      <Typography
        textAlign={"center"}
        variant={isMobileOrTablet ? "body2" : "body1"}
        color={theme?.palette?.primary?.dark}
        fontWeight={600}
      >
        {formatValue(value)}
      </Typography>
    </Box>
  );
};

const CardDivider = () => {
  const theme = useTheme();
  return (
    <Box width="1px" height="50px">
      <Divider sx={{ backgroundColor: theme.palette.divider }} orientation="vertical" />
    </Box>
  );
};

const FirmaDocumentos: React.FC = () => {
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const interfaceConfig = useInterfaceConfig();

  const application = useSelector((state: RootState) => state.register.application);
  const accountUser = useSelector((state: RootState) => state.register.account_user);

  const [getApplicationConditions] = useLazyGetLoanApplicationConditionsByIdQuery();
  const [getApplicationById] = useLazyGetLoanApplicationByIdQuery();
  const [createDocument] = usePostDocumentGenarateMutation();
  const [getTablaAmortizacion] = useLazyGetTablaAmortizacionQuery();
  const [triggerPostNextStage] = usePostValidateNextStageMutation();

  const [videoUrl, setVideoUrl] = React.useState<string>("");
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [videoFinished, setVideoFinished] = React.useState(false);
  const [termsAccepted, setTermsAccepted] = React.useState(false);
  const [viewParams, setViewParams] = React.useState<viewParameters>({
    monto: 0,
    plazo: 0,
    tasa: 0,
    comisionPorcentual: 0,
    valorComision: 0,
    primerPago: 0,
    pagosConsecuentes: 0,
  });

  const handleContinue = async () => {
    try {
      setIsLoading(true);

      const { code: next_status, url: next_url } = STATUS_CATALOG.firma_representante_legal;

      await triggerPostNextStage({
        applicationId: application.id,
        statusCode: next_status,
        username: `BOFU - ${accountUser.email}`,
      }).unwrap();

      navigate(next_url);
    } catch (error) {
      const api_error = error as ApiError;
      Sentry.captureException(error, {
        extra: {
          component: "BienvenidaVideo-submit",
        },
      });
      dispatch(setModalErrorVisible({ open: true, error: api_error }));
    } finally {
      setIsLoading(false);
    }
  };

  const generateTablaAmortizacion = async () => {
    try {
      // setIsLoading(true);
      const data = {
        loan_application_id: application.id,
        document_type: "tabla_amortizacion",
      };

      const document_url = await createDocument(data).unwrap();
      openFileInNewTab(document_url as unknown as string);
    } catch (error) {
      const api_error = error as ApiError;
      Sentry.captureException(error, {
        extra: {
          component: "BienvenidaVideo-TablaAmortizacion",
        },
      });
      dispatch(setModalErrorVisible({ open: true, error: api_error }));
    } finally {
      // setIsLoading(false);
    }
  };

  const openFileInNewTab = (fileUrl: string) => {
    const link = document.createElement("a");
    link.href = fileUrl;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);

        const { firma } = interfaceConfig;

        if (firma?.video_url) {
          setVideoUrl(firma.video_url);
          setVideoFinished(false);
        } else {
          setVideoUrl("");
          setVideoFinished(true);
        }

        const [loanApplication, loanCondition, tablaAmortizacion] = await Promise.all([
          getApplicationById(application.id).unwrap(),
          getApplicationConditions(application.id).unwrap(),
          getTablaAmortizacion({
            loanApplicationId: application.id,
          }).unwrap(),
        ]);

        if (loanCondition) {
          const montoAutorizado = loanCondition.selected_amount || 0;
          const porcentajeComision = loanApplication.opening_fee || 0;
          const valorComision = (montoAutorizado * porcentajeComision) / 100;
          const primerPago = tablaAmortizacion.find((item) => item.numero_pago === 1);
          const segundoPago = tablaAmortizacion.find((item) => item.numero_pago === 2);

          setViewParams({
            monto: montoAutorizado,
            plazo: loanCondition.selected_term,
            tasa: loanCondition.interest_rate,
            comisionPorcentual: porcentajeComision,
            valorComision: Math.round(valorComision * 100) / 100,
            primerPago: primerPago ? +primerPago.monto_total : 0,
            pagosConsecuentes: segundoPago ? +segundoPago.monto_total : 0,
          });
        }
      } catch (error) {
        const api_error = error as ApiError;
        Sentry.captureException(error, {
          extra: {
            component: "BienvenidaVideo-initial_fetch",
          },
        });
        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  return (
    <Grid container height="100%">
      <Grid item xs={12} md={8}>
        <InactivityHandler />

        <Container maxWidth="lg">
          <Typography
            mt={5}
            variant={isMobileOrTablet ? "h5" : "h4"}
            fontWeight={700}
            color={theme?.palette?.primary?.dark}
            textAlign="center"
          >
            Firma de documentos
          </Typography>

          <NavBarOcularVisit currentStep={4} />

          {isLoading && (
            <Box mt={4} display="flex" flexDirection="column" alignItems="center">
              <Skeleton variant="rectangular" sx={{ height: "190px", width: "100%", borderRadius: 2 }} />
            </Box>
          )}
          {!isLoading && (
            <Box>
              {videoUrl && (
                <Box display="flex" justifyContent="center" mt={4}>
                  <Typography
                    textAlign={"center"}
                    variant={isMobileOrTablet ? "body2" : "body1"}
                    color={theme?.palette?.primary?.main}
                    fontWeight={600}
                  >
                    Antes de proceder a la firmar, visualiza este video con la información esencial de tu crédito y
                    acepta sus condiciones finales.
                  </Typography>
                </Box>
              )}

              {videoUrl && (
                <Box display="flex" justifyContent="center" mt={4}>
                  <ReactPlayer
                    url={videoUrl}
                    playing={false}
                    muted={false}
                    onStart={() => {
                      console.log("Video started");
                    }}
                    onEnded={() => {
                      console.log("Video ended");
                      setVideoFinished(true);
                    }}
                    controls={true}
                    width={isMobileOrTablet ? "100%" : 300}
                    height={isMobileOrTablet ? 300 : 500}
                  />
                </Box>
              )}

              <Box mt={isMobileOrTablet ? 1 : 2} display="flex" flexDirection="column" alignItems="center">
                <Typography
                  textAlign={"center"}
                  variant={isMobileOrTablet ? "body2" : "body1"}
                  color={theme?.palette?.primary?.main}
                  fontWeight={600}
                >
                  Condiciones del Crédito
                </Typography>

                <Box width="100%" display="flex" justifyContent="center" mt={1}>
                  <Box display="flex" justifyContent="center" width={isMobileOrTablet ? "100%" : "60%"}>
                    <CardInfo title="Monto del crédito" value={viewParams?.monto} valueType="currency" width="30%" />
                    <CardInfo title="Plazo" value={viewParams?.plazo} valueType="months" width="30%" />
                  </Box>
                </Box>
              </Box>

              <Box mt={isMobileOrTablet ? 1 : 2} display="flex" flexDirection="column" alignItems="center">
                <Typography
                  textAlign={"center"}
                  variant={isMobileOrTablet ? "body2" : "body1"}
                  color={theme?.palette?.primary?.main}
                  fontWeight={600}
                >
                  Calendario de Pagos
                </Typography>

                <Box width="100%" display="flex" justifyContent="center" mt={1}>
                  <Box display="flex" justifyContent="center" width={isMobileOrTablet ? "100%" : "60%"}>
                    <CardInfo title="Primer Pago" value={viewParams.primerPago} valueType="currency" width="40%" />
                    <CardDivider />
                    <CardInfo
                      title="Pagos Consecuentes"
                      value={viewParams.pagosConsecuentes}
                      valueType="currency"
                      width="40%"
                    />
                  </Box>
                </Box>
              </Box>

              <Box mt={isMobileOrTablet ? 1 : 2} display="flex" flexDirection="column" alignItems="center">
                <Typography
                  textAlign={"center"}
                  variant={isMobileOrTablet ? "body2" : "body1"}
                  color={theme?.palette?.primary?.dark}
                >
                  Fecha de Corte
                </Typography>

                <Typography
                  textAlign={"center"}
                  variant={isMobileOrTablet ? "body2" : "body1"}
                  color={theme?.palette?.primary?.main}
                  fontWeight={600}
                >
                  Día 5 de cada mes
                </Typography>
              </Box>

              <Box display="flex" flexDirection="column" alignItems="center" mt={isMobileOrTablet ? 1 : 2}>
                <Button variant="text" onClick={generateTablaAmortizacion} disabled={isLoading}>
                  <Typography
                    variant={isMobileOrTablet ? "body2" : "body1"}
                    color={theme?.palette?.primary?.dark}
                    align="center"
                  >
                    <img src={iconD} /> Descarga aquí tu tabla de amortización
                  </Typography>
                </Button>
              </Box>

              <Box mt={isMobileOrTablet ? 1 : 2} display="flex" flexDirection="column" alignItems="center">
                <Box width="100%" display="flex" justifyContent="center" alignItems="center" mt={1}>
                  <CustomSwitch
                    id="authorize"
                    name="authorize"
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setTermsAccepted(event.target.checked);
                    }}
                    disabled={isLoading}
                  />
                  <Typography
                    textAlign={"left"}
                    variant={isMobileOrTablet ? "body2" : "body1"}
                    color={theme?.palette?.primary?.dark}
                    width={isMobileOrTablet ? "90%" : "50%"}
                  >
                    Declaro, bajo protesta de decir verdad, comprender las condiciones de mi crédito y el calendario de
                    pagos.
                  </Typography>
                </Box>
              </Box>

              <Box mt={2} display="flex" flexDirection="column" alignItems="center">
                <Button
                  variant="default"
                  type="submit"
                  disabled={!videoFinished || !termsAccepted || isLoading}
                  sx={{
                    mt: 2,
                    padding: "12px",
                    width: isMobileOrTablet ? "100%" : "200px",
                    textTransform: "none",
                  }}
                  onClick={handleContinue}
                >
                  Continuar
                </Button>
              </Box>
            </Box>
          )}
        </Container>
      </Grid>
      {!isMobileOrTablet && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}
    </Grid>
  );
};

export default FirmaDocumentos;
